// NOTE: This is the FE-side field configuration for the mapping UI.
// It must stay aligned with the BE canonical contract in:
//   compliance_reporting_server/v2/ptrs/contracts/ptrs.canonical.contract.js
// using FE-friendly (camelCase) field IDs.
//
// Rule of thumb:
// - PTRS_REQUIRED_FIELDS / PTRS_OPTIONAL_FIELDS should reflect the BE canonical contract.
// - FE-only derived/system helpers can still exist, but they should be clearly marked as supplemental.

// -----------------------------------------------------------------------------
// Required canonical fields (aligned with BE canonical contract)
// -----------------------------------------------------------------------------
export const PTRS_REQUIRED_FIELDS = [
  // Identity
  "payerEntityName",
  "payerEntityAbn",
  "payeeEntityName",
  "payeeEntityAbn",
  "invoiceReferenceNumber",

  // Transaction values
  "paymentAmount",
  "paymentDate",
];

// -----------------------------------------------------------------------------
// Optional canonical fields (useful for completeness / QA, but not MVP blockers)
// -----------------------------------------------------------------------------
export const PTRS_OPTIONAL_FIELDS = [
  // Identity extras
  "payerEntityAcnArbn",
  "payeeEntityAcnArbn",

  // Transaction values
  "description",

  // Raw dates
  "supplyDate",
  "noticeForPaymentIssueDate",
  "invoiceIssueDate",
  "invoiceReceiptDate",
  "invoiceDueDate",

  // Raw payment term sources
  "contractPoReferenceNumber",
  "contractPoPaymentTerms",
  "noticeForPaymentTerms",
  "invoicePaymentTerms",
  "paymentTerm",
  "documentType",
  "companyCode",
  "documentCurrency",
  "clearingDocument",
  "sourceAccountCode",

  // Regulator classification flags
  "tradeCreditPayment",
  "excludedTradeCreditPayment",
  "peppolEinvoiceEnabled",
  "rcti",
  "creditCardPayment",
  "creditCardNo",
  "partialPayment",

  // -------------------------------------------------------------------------
  // FE/system supplemental fields (not part of the BE canonical contract)
  // Keep these explicit so they are not mistaken for canonical contract fields.
  // -------------------------------------------------------------------------
  "paymentTimeReferenceDate",
  "paymentTimeReferenceKind",
  "paymentTermSource",
  "paymentTermDays",
  "paymentTimeDays",
  "isSmallBusiness",
  "excludeFromMetrics",
  "excludeComment",
  "excludeSetAt",
  "excludeSetBy",

  // Processing fields (used for staging logic, not part of BE canonical contract)
  "invoiceCreatedDate",
  "entryDate",
  "reconciliationStatus",
  "sourceUser",
];

// -----------------------------------------------------------------------------
// Grouped mapping requirements (MVP UX rules layered on top of the BE contract)
// -----------------------------------------------------------------------------
export const PTRS_REQUIRED_FIELD_GROUPS = [
  {
    id: "paymentClockStart",
    label: "Payment timing start date",
    description:
      "Map at least one date that can be used as the start date for calculating payment time.",
    fields: [
      "invoiceIssueDate",
      "invoiceReceiptDate",
      "noticeForPaymentIssueDate",
      "supplyDate",
    ],
    minRequired: 1,
  },
];

const SAP_ACCOUNTING_REQUIRED_FIELDS = [
  ...PTRS_REQUIRED_FIELDS,
  "documentType",
  "companyCode",
  "sourceAccountCode",
  "clearingDocument",
];

export const PTRS_ADAPTER_MAPPING_REQUIREMENTS = Object.freeze({
  sap_accounting_event: Object.freeze({
    requiredFields: Object.freeze(SAP_ACCOUNTING_REQUIRED_FIELDS),
    requiredFieldGroups: Object.freeze(PTRS_REQUIRED_FIELD_GROUPS),
  }),
  xero_accounting_event: Object.freeze({
    requiredFields: Object.freeze(PTRS_REQUIRED_FIELDS),
    requiredFieldGroups: Object.freeze(PTRS_REQUIRED_FIELD_GROUPS),
  }),
  direct_payment: Object.freeze({
    requiredFields: Object.freeze(
      PTRS_REQUIRED_FIELDS.filter(
        (field) => !["payerEntityName", "payerEntityAbn"].includes(field),
      ),
    ),
    requiredFieldGroups: Object.freeze(PTRS_REQUIRED_FIELD_GROUPS),
  }),
});

export const PTRS_ADAPTER_LABELS = Object.freeze({
  sap_accounting_event: "SAP accounting transactions",
  xero_accounting_event: "Xero accounting transactions",
  direct_payment: "Direct / self-contained transactions",
});

export function getPtrsAdapterMappingRequirements(adapterType) {
  return (
    PTRS_ADAPTER_MAPPING_REQUIREMENTS[adapterType] ||
    PTRS_ADAPTER_MAPPING_REQUIREMENTS.sap_accounting_event
  );
}

export function getPtrsAdapterLabel(adapterType) {
  return PTRS_ADAPTER_LABELS[adapterType] || "Unknown transaction source";
}

const normaliseCanonicalFieldId = (value) =>
  String(value || "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();

export function getPtrsDatasetMappingReadiness(adapterType, fieldMap = []) {
  const requirements = getPtrsAdapterMappingRequirements(adapterType);
  const mapped = new Set(
    (Array.isArray(fieldMap) ? fieldMap : [])
      .map((row) => normaliseCanonicalFieldId(row?.canonicalField))
      .filter(Boolean),
  );
  const missingFields = requirements.requiredFields.filter(
    (field) => !mapped.has(normaliseCanonicalFieldId(field)),
  );
  const missingGroups = requirements.requiredFieldGroups.filter(
    (group) =>
      !group.fields.some((field) =>
        mapped.has(normaliseCanonicalFieldId(field)),
      ),
  );
  return {
    ready: missingFields.length === 0 && missingGroups.length === 0,
    missingFields,
    missingGroups,
  };
}

export function getReachablePtrsDatasetIds(
  datasets = [],
  joins = [],
  selectedId,
) {
  const selected = String(selectedId || "");
  const transactionIds = new Set(
    datasets
      .filter((dataset) => dataset?.purpose === "transaction")
      .map((dataset) => String(dataset.id)),
  );
  const reachable = new Set(selected ? [selected] : []);
  let changed = true;
  while (changed) {
    changed = false;
    for (const condition of Array.isArray(joins) ? joins : []) {
      const fromId = String(condition?.from?.datasetId || "");
      const toId = String(condition?.to?.datasetId || "");
      if (!fromId || !toId) continue;
      if (
        reachable.has(fromId) &&
        !reachable.has(toId) &&
        (!transactionIds.has(toId) || toId === selected)
      ) {
        reachable.add(toId);
        changed = true;
      }
      if (
        reachable.has(toId) &&
        !reachable.has(fromId) &&
        (!transactionIds.has(fromId) || fromId === selected)
      ) {
        reachable.add(fromId);
        changed = true;
      }
    }
  }
  return reachable;
}

// -----------------------------------------------------------------------------
// Human-readable labels (MapPanel should prefer these for display)
// -----------------------------------------------------------------------------
export const PTRS_FIELD_LABELS = {
  // Identity
  payerEntityName: "Payer name",
  payerEntityAbn: "Payer ABN",
  payerEntityAcnArbn: "Payer ACN/ARBN",
  payeeEntityName: "Payee name",
  payeeEntityAbn: "Payee ABN",
  payeeEntityAbnValid: "Payee ABN valid (checksum)",
  payeeEntityAcnArbn: "Payee ACN/ARBN",
  invoiceReferenceNumber: "Invoice reference number",

  // Amounts / dates
  paymentAmount: "Payment amount",
  paymentDate: "Payment date",
  paymentTimeReferenceDate: "Payment time reference date",
  paymentTimeReferenceKind: "Payment time reference kind (provenance)",
  paymentTimeDays: "Payment time (days, derived)",

  // Terms
  paymentTerm: "Payment term (raw text)",
  documentType: "Document type",
  companyCode: "Company code",
  documentCurrency: "Document currency",
  clearingDocument: "Clearing document",
  sourceAccountCode: "Source account code",
  paymentTermDays: "Payment term (days, derived)",
  paymentTermSource: "Payment term source (provenance)",
  contractPoReferenceNumber: "Contract/PO reference number",
  contractPoPaymentTerms: "Contract/PO payment terms",
  noticeForPaymentIssueDate: "Notice for payment issue date",
  noticeForPaymentTerms: "Notice for payment terms",
  invoicePaymentTerms: "Invoice payment terms",
  invoiceIssueDate: "Invoice issue date",
  invoiceReceiptDate: "Invoice receipt date",
  invoiceDueDate: "Invoice due date",
  invoiceCreatedDate: "Invoice created date (processing)",
  entryDate: "Entry date (processing)",
  reconciliationStatus: "Reconciliation status (processing)",
  sourceUser: "User (processing)",
  supplyDate: "Supply date",

  // Flags
  tradeCreditPayment: "Trade credit payment (flag)",
  excludedTradeCreditPayment: "Excluded trade credit payment (flag)",
  isSmallBusiness: "Is small business (SBI outcome)",
  peppolEinvoiceEnabled: "Peppol e-invoice enabled",
  rcti: "RCTI",
  creditCardPayment: "Credit card payment",
  creditCardNo: "Credit card number",
  partialPayment: "Partial payment",

  // Exclusions / FE system controls (supplemental, not BE canonical contract fields)
  excludeFromMetrics: "Exclude from metrics",
  excludeComment: "Exclude comment",
  excludeSetAt: "Exclude set at",
  excludeSetBy: "Exclude set by",

  // Other
  description: "Description",
};

// -----------------------------------------------------------------------------
// Simple synonyms to power auto-suggest
// -----------------------------------------------------------------------------
export const FIELD_SYNONYMS = {
  payerEntityName: ["payer name", "org name", "organisation", "organization"],
  payerEntityAbn: ["payer abn", "org abn", "organisation abn", "abn"],
  payeeEntityName: ["payee name", "supplier name", "vendor name", "name"],
  payeeEntityAbn: ["payee abn", "supplier abn", "vendor abn"],
  invoiceReferenceNumber: [
    "invoice reference",
    "invoice id",
    "invoice number",
    "reference",
  ],
  paymentAmount: ["payment amount", "amount", "paid amount", "payment value"],
  paymentDate: ["payment date", "clearing date", "paid on"],
  paymentTermDays: ["payment term days", "terms days", "net days"],
  paymentTerm: ["payment terms", "terms", "net terms"],
  documentType: ["document type", "doc type"],
  documentCurrency: ["document currency", "currency", "doc currency"],
  clearingDocument: ["clearing document", "clearing doc"],
  sourceAccountCode: ["account code", "source account code", "vendor code"],
};
